import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import Select from 'react-select';
import Constants from '../../config/constants';
import Layout from '../../components/layout/Layout';
import Thumbnail from '../../components/thumbnail/Thumbnail';
import DataTable from '../../components/datatable/DataTable';
import Alert from '../../modal/alert/Alert';
import ACSplitTypeCreateModal from '../../modal/ac_split_type/ACSplitTypeCreateModal';
import ACSplitTypeEditModal from '../../modal/ac_split_type/ACSplitTypeEditModal';
import ACSplitTypeDuplicateModal from '../../modal/duplicate/ACSplitTypeDuplicateModal';
import { COLUMN_STYLE, getColumnStyle } from '../../components/datatable/util';
import {
  defaultRejectHandler,
  fromCoolingCap,
  getErrorValue,
  getStatusStyle,
  withDefaultSort,
} from '../../utils/utility';
import {
  listACSplitType,
  deleteACSplitType,
  clearErrorACSplit,
} from '../../redux/ac_split_type/ac_split_type_actions';

export class ACSplitTypePage extends React.Component {
  constructor(props) {
    super(props);
    const { t } = props;

    this.search = {
      pageSize: Constants.DATA_TABLE_PAGE_SIZE,
      page: null,
      sorted: null,
      filtered: null,
    };

    const textColumn = getColumnStyle(COLUMN_STYLE.text);
    const coeColumn = getColumnStyle(COLUMN_STYLE.coefficient);
    const decimalColumn = getColumnStyle(COLUMN_STYLE.decimal);

    this.columns = [
      {
        Header: '',
        accessor: 'thumbnail',
        Cell: row => <Thumbnail img={row.value} />,
        ...getColumnStyle(COLUMN_STYLE.thumbnail),
      },
      {
        Header: () => <b>{t('name')}</b>,
        accessor: 'name',
        ...textColumn,
      },
      {
        Header: () => <b>{t('category')}</b>,
        accessor: 'is_public',
        Cell: row =>
          row.value ? (
            <span>
              <i className="fas fa-users" /> {t('public')}
            </span>
          ) : (
            <span>
              <i className="fas fa-user" /> {t('private')}
            </span>
          ),
        ...getColumnStyle(COLUMN_STYLE.category),
      },
      {
        Header: () => <b>{t('air type')}</b>,
        accessor: 'type',
        Cell: row => (row.value === 'FIXED' ? 'FIXED SPEED' : row.value),
        ...textColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('brand')}</b>,
        accessor: 'brand_name',
        ...textColumn,
        width: 100,
      },
      {
        id: 'cooling_capacity',
        Header: () => <b>{t('cooling capacity')}</b>,
        accessor: data => fromCoolingCap(data.cooling_capacity, data.unit),
        ...decimalColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('unit')}</b>,
        accessor: 'unit',
        Cell: row => (row.value === 'KBTU' ? 'Btu/h' : row.value),
        ...textColumn,
        width: 80,
      },
      {
        Header: () => <b>{t('power (kW)')}</b>,
        accessor: 'power_required',
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('COP')}</b>,
        accessor: 'COP',
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('SEER')}</b>,
        accessor: 'SEER',
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('compliance')}</b>,
        accessor: 'compliance',
        ...coeColumn,
        width: 100,
      },
      {
        // FRS 4.2.4 (Bug #6) - show which versioned criteria this AC was evaluated against.
        // null = ยังไม่มี snapshot (ยังไม่ถูกประเมินในโครงการ) -> แสดงข้อความให้ชัดแทน "-"
        Header: () => <b>{t('compliance version')}</b>,
        accessor: 'compliance_version',
        Cell: row =>
          row.value ? (
            <span title={t('compliance version')}>{row.value}</span>
          ) : (
            <span style={{ color: '#999' }}>{t('not evaluated yet')}</span>
          ),
        width: 120,
        ...textColumn,
      },
      {
        Header: () => <b>{t('status')}</b>,
        accessor: 'status',
        Cell: row => (
          <span className={getStatusStyle(row.value)}>
            {t(String(row.value).toLowerCase())}
          </span>
        ),
        width: 80,
        ...textColumn,
      },
      {
        Header: () => (
          <b>
            {t('cfp')}
            <br />({t('kgCO2eq per unit')})
          </b>
        ),
        accessor: 'cfp_value',
        Cell: row => `${Number(row.value || 0)} ${row.original.cfp_unit || ''}`,
      },
    ];

    this.state = {
      selectedItem: null,
      categoryOption: 'all',
      isShowCreateModal: false,
      isShowEditModal: false,
      isShowDeleteModal: false,
      isShowDuplicateModal: false,
      isCreated: false,
      isEdited: false,
      isDuplicated: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.isCreating &&
      nextProps.isCreating !== this.props.isCreating &&
      !nextProps.errorMessage
    ) {
      this.setState({ isShowCreateModal: false, isCreated: true });
    }

    if (
      this.props.isEditing &&
      nextProps.isEditing !== this.props.isEditing &&
      !nextProps.errorMessage
    ) {
      this.setState({ isShowEditModal: false, isEdited: true });
    }

    if (
      this.props.isDeleting &&
      nextProps.isDeleting !== this.props.isDeleting &&
      !nextProps.errorMessage
    ) {
      this.setState({ isShowDeleteModal: false });
    }

    if (
      this.props.isDuplicating &&
      nextProps.isDuplicating !== this.props.isDuplicating &&
      !nextProps.errorMessage
    ) {
      this.setState({ isShowDuplicateModal: false, isDuplicated: true });
    }
  }

  renderActionMenu() {
    return [
      <li>
        <a
          href="#"
          id="air-conditioning-split-type-create"
          data-toggle="modal"
          data-target="#create-ballast"
          onClick={() =>
            this.setState({ isShowCreateModal: true, isCreated: false })
          }
        >
          <i className="fas fa-plus-square" />
          <span className="menu-text">
            {this.props.t('create a/c split type')}
          </span>
        </a>
      </li>,
    ];
  }

  updateSortFilter(filter, state, category) {
    if (state) {
      this.search.pageSize = state.pageSize;
      this.search.page = state.page;
      this.search.sorted = state.sorted;
    }
    this.search.filtered = filter;

    this.props.listAc(
      this.search.pageSize,
      this.search.page,
      withDefaultSort(this.search.sorted),
      this.search.filtered,
      {
        all: null,
        public: true,
        private: false,
      }[category.value],
    );

    this.setState({ categoryOption: category });
  }

  dismissCreateModal() {
    this.props.clearError();
    this.setState({ isShowCreateModal: false });
  }

  dismissEditModal() {
    this.props.clearError();
    this.setState({ isShowEditModal: false });
  }

  dismissDeleteModal() {
    this.props.clearError();
    this.setState({ isShowDeleteModal: false });
  }

  dismissDuplicateModal() {
    this.props.clearError();
    this.setState({ isShowDuplicateModal: false });
  }

  render() {
    const {
      categoryOption,
      selectedItem: item,
      isShowCreateModal,
      isShowEditModal,
      isShowDeleteModal,
      isShowDuplicateModal,
    } = this.state;

    const {
      filtered,
      filteredCount,
      isLoading,
      isDeleting,
      isDuplicating,
      t,
      errorMessage,
    } = this.props;

    const breadcrumbItems = [
      { name: t('air conditioning system'), path: '/ac' },
      { name: t('split type'), path: '/ac/split_type' },
    ];

    const helpConfig = {
      title: t('help'),
      contentUrl: '/html/help_ac_split.html',
    };

    return (
      <Layout
        history={this.props.history}
        breadcrumbItems={breadcrumbItems}
        renderActionMenu={() => this.renderActionMenu()}
        help={helpConfig}
      >
        <ACSplitTypeCreateModal
          show={isShowCreateModal}
          isCreated={this.state.isCreated}
          onHide={() => this.dismissCreateModal()}
        />
        <ACSplitTypeEditModal
          ac={item}
          show={isShowEditModal}
          isEdited={this.state.isEdited}
          onHide={() => this.dismissEditModal()}
        />
        <ACSplitTypeDuplicateModal
          item={item}
          show={isShowDuplicateModal}
          title={t('copy a/c split type')}
          label={t('new a/c split type')}
          isDuplicated={this.state.isDuplicated}
          onHide={() => !isDuplicating && this.dismissDuplicateModal()}
        />
        <Alert
          show={isShowDeleteModal}
          onHide={() => !isDeleting && this.dismissDeleteModal()}
          title={t('delete a/c split type')}
          body={`${t('do you want to delete')} ${item ? item.name : ''}?`}
          errorMessage={getErrorValue(errorMessage, 'detail')}
          primaryButtonTitle={isDeleting ? t('deleting') : t('delete')}
          primaryButtonStyle={'danger'}
          primaryButtonEnabled={!isDeleting}
          onPrimaryButtonClick={() => this.props.deleteAC(item)}
        />
        <h1>{t('air conditioner split type')}</h1>
        <div style={{ backgroundColor: 'white' }}>
          <div
            className="filter-box"
            style={{ display: 'flex', padding: 20, marginBottom: 20 }}
          >
            <input
              id="air-conditioning-split-type-search-name"
              className="text-search"
              style={{ width: 240, height: 36, margin: 10 }}
              type="text"
              onChange={event =>
                this.updateSortFilter(event.target.value, null, categoryOption)
              }
              placeholder={t('search name')}
            />
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              style={{ width: 160, margin: 10 }}
              value={categoryOption}
              placeholder={t('category')}
              onChange={option => this.updateSortFilter(filtered, null, option)}
              options={[
                { value: 'all', label: t('all') },
                { value: 'public', label: t('public') },
                { value: 'private', label: t('private') },
              ]}
            />
          </div>
          <DataTable
            columns={this.columns}
            data={this.props.acList}
            onActionEdit={item =>
              this.setState({
                isShowEditModal: true,
                selectedItem: item.original,
                isEdited: false,
              })
            }
            onActionDuplicate={item =>
              this.setState({
                isShowDuplicateModal: true,
                selectedItem: item.original,
                isDuplicated: false,
              })
            }
            onActionDelete={item =>
              this.setState({
                isShowDeleteModal: true,
                selectedItem: item.original,
              })
            }
            filteredCount={filteredCount}
            isLoading={isLoading}
            onFetchData={state =>
              this.updateSortFilter(filtered, state, categoryOption)
            }
          />
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  acList: state.ac.splitType.orderedIds.map(
    id => state.ac.splitType.objects[id],
  ),
  filteredCount: state.ac.splitType.filteredCount,
  isLoading: state.ac.splitType.isLoading,
  isCreating: state.ac.splitType.isCreating,
  isEditing: state.ac.splitType.isEditing,
  isDeleting: state.ac.splitType.isDeleting,
  isDuplicating: state.ac.splitType.isDuplicating,
  errorMessage: state.ac.splitType.errorMessage,
});

const mapDispatchToProps = dispatch => ({
  listAc: (pageSize, page, sorted, filtered, isPublic) =>
    defaultRejectHandler(
      dispatch(listACSplitType(pageSize, page, sorted, filtered, isPublic)),
    ),
  deleteAC: ac => defaultRejectHandler(dispatch(deleteACSplitType(ac.id))),
  clearError: () => dispatch(clearErrorACSplit()),
});

export default translate('translations')(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(ACSplitTypePage),
);



// WEBPACK FOOTER //
// ./src/pages/ac_split_type/ACSplitTypePage.js